/** Shared compliance expiry helpers for member equipment (admin). */

export const COMPLIANCE_ALERT_DAYS = 20;

export const COMPLIANCE_FIELDS = [
  { key: 'rcValidUntil', label: 'RC' },
  { key: 'pucValidUntil', label: 'PUC' },
  { key: 'insuranceValidUntil', label: 'Insurance' },
  { key: 'fitnessValidUntil', label: 'Fitness' },
  { key: 'taxValidUntil', label: 'Tax' },
];

export function parseComplianceDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [y, m, d] = raw.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatComplianceDate(value) {
  const d = parseComplianceDate(value);
  if (!d) return String(value || '');
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd} / ${mm} / ${d.getFullYear()}`;
}

/** Alerts for compliance dates within [COMPLIANCE_ALERT_DAYS] or already expired. */
export function getComplianceExpiryAlerts(asset) {
  if (!asset) return [];
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const alerts = [];

  for (const { key, label } of COMPLIANCE_FIELDS) {
    if (asset.fieldRequirements?.[key] === 'hidden') continue;
    const raw = asset[key];
    const expiry = parseComplianceDate(raw);
    if (!expiry) continue;
    const daysLeft = Math.round(
      (expiry.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
    );
    if (daysLeft > COMPLIANCE_ALERT_DAYS) continue;
    const dateText = formatComplianceDate(raw);
    let message;
    if (daysLeft < 0) {
      const days = -daysLeft;
      message = `${label} expired ${days} day${days === 1 ? '' : 's'} ago (${dateText})`;
    } else if (daysLeft === 0) {
      message = `${label} expires today (${dateText})`;
    } else {
      message = `${label} expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${dateText})`;
    }
    alerts.push({
      key,
      label,
      dateText,
      daysLeft,
      message,
      shortMessage:
        daysLeft < 0
          ? `Expired ${-daysLeft} day${-daysLeft === 1 ? '' : 's'} ago`
          : daysLeft === 0
            ? 'Expires today'
            : `${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining`,
      isExpired: daysLeft < 0,
    });
  }

  alerts.sort((a, b) => a.daysLeft - b.daysLeft);
  return alerts;
}
